import { useState, useEffect, lazy, Suspense } from "react";
import {
  Brain, Code2, Activity, Gauge, Hand, Bot, Rocket, Cpu, Globe, Smartphone,
  ShieldCheck, Database, ArrowRight, Sparkles, CheckCircle2, LogIn, ChevronRight, Zap, Loader2,
  Mail, ExternalLink, X, GraduationCap, FlaskConical,
} from "lucide-react";
import { C, ROLE_META } from "./theme";
import { T, t, LanguagePicker } from "./i18n.jsx";
import portrait from "./assets/xiangen-hu.jpg";

// The "try before you sign up" wall pulls in the Runner + every paradigm engine on demand —
// keep it out of the eager landing bundle so first paint stays light.
const LandingDemos = lazy(() => import("./LandingDemos.jsx"));

/* ------------------------------------------------------------------ *
 * Landing — the public entry surface, shown when no session is active.
 * Primary CTA signs in via the OAuth gateway; the role cards double as
 * a dev preview into each dashboard. Palette + personality match the
 * Studio: cool indigo = structure, warm amber = live/timing, mono for
 * precision.
 * ------------------------------------------------------------------ */

/* ---- hero showcase: a rotating reel of live paradigm stimuli (pure SVG/text, frame-driven) ---- */
const triPath = (x, y, dir, sz) => dir === "right"
  ? `M${x - sz} ${y - sz} L${x + sz} ${y} L${x - sz} ${y + sz} Z`
  : `M${x + sz} ${y - sz} L${x - sz} ${y} L${x + sz} ${y + sz} Z`;
const Svg = ({ children }) => <svg viewBox="0 0 300 130" width="100%" height="132" preserveAspectRatio="xMidYMid meet">{children}</svg>;

const SHOWCASE = [
  { name: "Stroop", mrt: "612ms", acc: "94%", effect: ["Stroop", "+78ms", C.amber],
    stim: (f) => { const P = [["RED", C.red], ["GREEN", C.green], ["BLUE", C.indigo], ["AMBER", C.amber]]; const i = Math.floor(f / 7); return <div style={{ fontSize: 54, fontWeight: 800, color: P[(i + 2) % 4][1], letterSpacing: 1 }}>{P[i % 4][0]}</div>; } },
  { name: "Eriksen Flanker", mrt: "498ms", acc: "96%", effect: ["Flanker", "+52ms", C.amber],
    stim: (f) => { const dir = Math.floor(f / 11) % 2 ? "right" : "left"; const cong = Math.floor(f / 11 + 1) % 2 === 0; const fl = cong ? dir : (dir === "left" ? "right" : "left"); return <Svg>{[44, 98, 152, 206, 260].map((x, i) => <path key={x} d={triPath(x, 65, i === 2 ? dir : fl, 17)} fill={i === 2 ? C.amber : C.hi} />)}</Svg>; } },
  { name: "Simon", mrt: "455ms", acc: "95%", effect: ["Simon", "+39ms", C.amber],
    stim: (f) => { const left = Math.floor(f / 12) % 2 === 0; return <Svg><line x1="150" y1="22" x2="150" y2="108" stroke={C.border} strokeWidth="1.5" /><text x="150" y="71" textAnchor="middle" fontSize="22" fill={C.dim} fontWeight="300">+</text><circle cx={left ? 56 : 244} cy="65" r="26" fill={left ? C.green : C.indigo} /></Svg>; } },
  { name: "Go / No-Go", mrt: "430ms", acc: "97%", effect: ["Commission", "3%", C.green],
    stim: (f) => { const go = Math.floor(f / 10) % 4 !== 0; return <Svg>{go ? <circle cx="150" cy="65" r="34" fill={C.green} /> : <rect x="116" y="31" width="68" height="68" rx="6" fill={C.red} />}</Svg>; } },
  { name: "N-Back", mrt: "520ms", acc: "d′ 2.1", effect: ["Load", "2-back", C.purple],
    stim: (f) => { const P = ["B", "F", "K", "M", "Q", "R", "T"]; const i = Math.floor(f / 9); const m = i % 4 === 0; return <div style={{ fontSize: 62, fontWeight: 800, color: m ? C.green : C.hi, fontFamily: "var(--mono)" }}>{P[i % P.length]}</div>; } },
  { name: "Random-Dot Motion", mrt: "720ms", acc: "82%", effect: ["Coherence", "24%", C.amber],
    stim: (f) => <Svg><circle cx="150" cy="65" r="55" fill="#0A0D11" stroke={C.border} />{Array.from({ length: 30 }).map((_, i) => { const coh = i % 4 === 0; const r = 8 + (i * 13) % 44; const a = ((i * 53) % 360) * Math.PI / 180; const bx = 150 + r * Math.cos(a), by = 65 + r * Math.sin(a); if (coh) { const x = 100 + ((bx - 100 + f * 4) % 100); return <line key={i} x1={x} y1={by} x2={x + 8} y2={by} stroke={C.amber} strokeWidth="2" />; } return <circle key={i} cx={bx + Math.sin(f / 6 + i) * 3} cy={by + Math.cos(f / 6 + i) * 3} r="1.7" fill={C.mid} />; })}</Svg> },
  { name: "Visual Search", mrt: "880ms", acc: "93%", effect: ["Slope", "26ms/item", C.amber],
    stim: (f) => { const tgt = Math.floor(f / 13) % 12; return <Svg>{Array.from({ length: 12 }).map((_, i) => { const x = 54 + (i % 4) * 64, y = 26 + Math.floor(i / 4) * 38; const isT = i === tgt; return <g key={i} transform={`rotate(${isT ? 0 : 40} ${x} ${y})`}><rect x={x - 6} y={y - 11} width="12" height="22" rx="2" fill={isT ? C.red : C.indigo} opacity={isT ? 1 : 0.7} /></g>; })}</Svg>; } },
  { name: "Mental Rotation", mrt: "1.2s", acc: "90%", effect: ["Slope", "12ms/°", C.amber],
    stim: (f) => <Svg>{[[95, 0], [205, (f * 5) % 360]].map(([cx, rot], i) => <g key={i} transform={`translate(${cx},65) rotate(${rot})`}><path d="M-18 -10 h18 v-12 h11 v32 h-29 Z" fill={i ? C.purple : C.indigo} opacity="0.9" /></g>)}</Svg> },
];

const FEATURES = [
  { icon: Code2, c: C.indigo, t: "Declarative specs", b: "Experiments are plain, diffable JSON — versioned in git, reproducible by hash, and editable by AI and humans alike." },
  { icon: Gauge, c: C.amber, t: "Frame-locked timing", b: "performance.now() presentation with TTL / photodiode triggers. We surface the precision each target can actually reach." },
  { icon: Activity, c: C.green, t: "Adaptive engine", b: "Trials chosen at runtime — weighted staircases, QUEST+, Bayesian psychometrics, IRT-driven item selection." },
  { icon: Hand, c: C.purple, t: "Multimodal capture", b: "Keyboard, voice (Web Audio), and webcam gaze recorded in parallel, with quality flags on the live stream." },
  { icon: Bot, c: C.green, t: "AI objects", b: "LLM-generated stimuli and rubric-scored debriefs through a single, accountable model gateway." },
  { icon: Rocket, c: C.indigo, t: "Deploy anywhere", b: "One spec compiles to lab desktop, browser, mobile, and fMRI / EEG-synced builds — no rewrite." },
];

const ROLE_BLURB = {
  admin: "Run the platform: people & roles, every study, backbone health, and model spend.",
  researcher: "Own studies end-to-end: design specs, assign RAs, and read aggregate results.",
  experimenter: "Operate sessions: manage the roster, launch runs, and watch live data quality.",
  experimenterAlt: "",
  participant: "Take assigned studies in the browser: consent, run a session, see your progress.",
};

const TARGETS = [
  { icon: Cpu, c: C.green, name: "Lab", note: "sub-ms · TTL" },
  { icon: Globe, c: C.amber, name: "Web", note: "frame-level" },
  { icon: Smartphone, c: C.indigo, name: "Mobile", note: "touch · EMA" },
  { icon: Brain, c: C.purple, name: "fMRI / EEG", note: "scanner-locked" },
];

export default function Landing({ health, onSignIn, onPreview }) {
  const b = health?.backbone || {};
  const [showAbout, setShowAbout] = useState(false);

  return (
    <div style={s.page}>
      <style>{css}</style>
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {/* nav */}
      <header style={s.nav}>
        <div style={s.brand}>
          <div style={s.logo}><Brain size={16} color={C.bg} /></div>
          <div style={s.brandName}>Onset<span style={{ color: C.dim, fontWeight: 400 }}> · Experiment Studio</span></div>
        </div>
        <nav style={s.navLinks}>
          <a href="#library" style={s.navLink}><T>Try a demo</T></a>
          <a href="#features" style={s.navLink}><T>Features</T></a>
          <a href="#roles" style={s.navLink}><T>For your team</T></a>
          <a href="#deploy" style={s.navLink}><T>Deploy</T></a>
          <button style={{ ...s.navLink, background: "none", border: "none", cursor: "pointer", fontFamily: "var(--ui)" }} onClick={() => setShowAbout(true)}><T>About</T></button>
          <LanguagePicker compact style={s.signin} />
          <button className="cta" style={s.signin} onClick={onSignIn}><LogIn size={14} /> <T>Sign in</T></button>
        </nav>
      </header>

      {/* hero */}
      <section style={s.hero}>
        <div style={s.heroL}>
          <div className="chip" style={s.chip}><Sparkles size={12} color={C.amber} /> <T>Design rigorous experiments. Run them anywhere. Stream the data live.</T></div>
          <h1 style={s.h1}>
            <T>Design experiments as</T> <span style={{ color: C.indigo }}><T>specs</T></span>.<br />
            <T>Run them</T> <span style={{ color: C.amber }}><T>anywhere</T></span>. <T>Watch the data</T> <span style={{ color: C.green }}><T>live</T></span>.
          </h1>
          <T comp="p" style={s.lead}>
            Onset turns rigorous psychology paradigms into declarative, version-controlled specs —
            with frame-locked timing, adaptive staircases, multimodal capture, and one-click deploy
            to lab, web, mobile, and scanner-synced targets.
          </T>
          <div style={s.heroCtas}>
            <button className="cta" style={s.primary} onClick={onSignIn}><T>Get started</T> <ArrowRight size={15} /></button>
            <a href="#roles" className="ghost" style={s.secondary}><T>Explore the dashboards</T></a>
          </div>
          <div style={s.trust}>
            <span style={s.trustItem}><CheckCircle2 size={13} color={C.green} /> <T>Reproducible by hash</T></span>
            <span style={s.trustItem}><CheckCircle2 size={13} color={C.green} /> <T>GCS-backed persistence</T></span>
            <span style={s.trustItem}><CheckCircle2 size={13} color={C.green} /> <T>Role-based access</T></span>
          </div>
        </div>

        {/* hero visual: a live presentation stage that reels through paradigms */}
        <div style={s.heroR}>
          <HeroStage />
        </div>
      </section>

      {/* try-it library — one runnable demo per category, no sign-in needed */}
      <section id="library" style={s.section}>
        <SectionHead eyebrow="See it before you sign up" title="Try a live experiment from every category"
          sub="Take any one right here in your browser — it's the real paradigm. Nothing is recorded, and you can close it anytime." />
        <Suspense fallback={<div style={s.demoLoading}><Loader2 size={15} className="spin" /> <T>Loading live demos…</T></div>}>
          <LandingDemos />
        </Suspense>
      </section>

      {/* features */}
      <section id="features" style={s.section}>
        <SectionHead eyebrow="Capabilities" title="Everything a modern paradigm needs" />
        <div style={s.fGrid}>
          {FEATURES.map((f) => {
            const I = f.icon;
            return (
              <div key={f.t} className="card" style={s.fCard}>
                <div style={{ ...s.fIcon, background: f.c + "1f" }}><I size={18} color={f.c} /></div>
                <div style={s.fTitle}><T>{f.t}</T></div>
                <div style={s.fBody}><T>{f.b}</T></div>
              </div>
            );
          })}
        </div>
      </section>

      {/* roles */}
      <section id="roles" style={s.section}>
        <SectionHead eyebrow="For your whole team" title="A dashboard tuned to each person" sub="Sign in to land on yours — or preview any of them now." />
        <div style={s.rGrid}>
          {["admin", "researcher", "experimenter", "participant"].map((r) => {
            const m = ROLE_META[r];
            return (
              <button key={r} className="card roleCard" style={{ ...s.rCard, ["--accent"]: m.c }} onClick={() => onPreview && onPreview(r)}>
                <div style={{ ...s.rDot, background: m.c }} />
                <div style={s.rName}><T>{m.label}</T></div>
                <div style={s.rDash}><T>{m.blurb}</T></div>
                <div style={s.rBlurb}><T>{ROLE_BLURB[r]}</T></div>
                <div style={s.rGo}><T>Preview</T> <ArrowRight size={13} /></div>
              </button>
            );
          })}
        </div>
      </section>

      {/* deploy */}
      <section id="deploy" style={s.section}>
        <SectionHead eyebrow="One spec, many targets" title="Deploy without a rewrite" />
        <div style={s.targets}>
          {TARGETS.map((t) => {
            const I = t.icon;
            return (
              <div key={t.name} className="card" style={s.tCard}>
                <div style={{ ...s.fIcon, background: t.c + "1f", margin: "0 auto 10px" }}><I size={18} color={t.c} /></div>
                <div style={{ color: C.hi, fontWeight: 700, fontSize: 14 }}><T>{t.name}</T></div>
                <div style={{ color: t.c, fontSize: 11, fontFamily: "var(--mono)", marginTop: 2 }}><T>{t.note}</T></div>
              </div>
            );
          })}
        </div>
        <div style={s.honest}>
          <ShieldCheck size={16} color={C.amber} />
          <span><b style={{ color: C.hi }}><T>An honest note on timing.</T></b> <T>Browser builds are frame-bounded — response recording can lag tens of ms.
            Lab/native builds reach sub-millisecond precision with TTL/photodiode triggers. Onset surfaces the achievable precision per
            target instead of hiding it.</T></span>
        </div>
      </section>

      {/* final CTA */}
      <section style={s.cta}>
        <T comp="h2" style={s.ctaH}>Ready to run your next study?</T>
        <T comp="p" style={{ color: C.mid, fontSize: 15, maxWidth: 560, margin: "0 auto 22px" }}>
          Sign in to design a spec, recruit participants, and collect frame-accurate data — all in one place.
        </T>
        <button className="cta" style={{ ...s.primary, fontSize: 15, padding: "12px 24px" }} onClick={onSignIn}><T>Sign in to Onset</T> <ArrowRight size={16} /></button>
      </section>

      {/* footer */}
      <footer style={s.footer}>
        <div style={s.brand}>
          <div style={{ ...s.logo, width: 24, height: 24 }}><Brain size={13} color={C.bg} /></div>
          <span style={{ color: C.mid, fontSize: 13 }}>Onset — <T>Experiment Studio</T></span>
          <button style={{ ...s.navLink, background: "none", border: "none", cursor: "pointer", fontFamily: "var(--ui)", padding: 0 }} onClick={() => setShowAbout(true)}><T>About the creator</T></button>
        </div>
        <div style={s.fStatus}>
          <span style={s.statItem}><Database size={12} color={health ? C.green : C.dim} /> <T>store:</T> {b.persistence || "…"}</span>
          <span style={s.statItem}><ShieldCheck size={12} color={b.llmGateway ? C.green : C.dim} /> <T>LLM gateway:</T> {b.llmGateway ? t("live") : t("off")}</span>
          <span style={s.statItem}><Cpu size={12} color={C.amber} /> <T>clock:</T> performance.now()</span>
        </div>
      </footer>
    </div>
  );
}

export function HeroStage() {
  const [frame, setFrame] = useState(0);
  const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    const id = setInterval(() => setFrame((f) => f + 1), reduce ? 2400 : 70);
    return () => clearInterval(id);
  }, [reduce]);
  const PER = reduce ? 1 : 34;                 // frames each paradigm holds the stage (~2.4s)
  const pi = Math.floor(frame / PER) % SHOWCASE.length;
  const item = SHOWCASE[pi];
  return (
    <div className="float" style={s.stageCard}>
      <div style={s.stageBar}>
        <span style={{ color: C.amber, fontSize: 11, fontFamily: "var(--mono)" }}>● <T>REC</T> · {item.name}</span>
        <span style={{ color: C.dim, fontSize: 11, fontFamily: "var(--mono)" }}>{pi + 1}/{SHOWCASE.length}</span>
      </div>
      <div style={s.stage}>
        <div key={pi} className="pop" style={{ width: "100%", display: "grid", placeItems: "center" }}>{item.stim(frame)}</div>
      </div>
      <div style={s.timeline}>
        {[["Fixation", "500ms", C.dim], ["Stimulus", "≤2000ms", C.amber], ["Feedback", "500ms", C.dim]].map(([l, ms, col], i) => (
          <div key={l} style={{ display: "flex", alignItems: "center", flex: l === "Stimulus" ? 1.6 : 1 }}>
            <div style={{ ...s.tl, borderColor: col === C.amber ? C.amber : C.border, background: col === C.amber ? "rgba(242,169,59,0.12)" : C.raised, flex: 1 }}>
              <span style={{ color: col === C.amber ? C.amber : C.hi, fontWeight: 600, fontSize: 11.5, display: "flex", gap: 4, alignItems: "center" }}>{col === C.amber && <Zap size={10} />}<T>{l}</T></span>
              <span style={{ color: C.dim, fontSize: 10.5, fontFamily: "var(--mono)" }}>{ms}</span>
            </div>
            {i < 2 && <ChevronRight size={13} color={C.dim} />}
          </div>
        ))}
      </div>
      <div style={s.metrics}>
        <Mini label="Mean RT" val={item.mrt} c={C.indigo} />
        <Mini label="Accuracy" val={item.acc} c={C.green} />
        <Mini label={item.effect[0]} val={item.effect[1]} c={item.effect[2]} />
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div style={{ textAlign: "center", marginBottom: 30 }}>
      <div style={s.eyebrow}><T>{eyebrow}</T></div>
      <T comp="h2" style={s.h2}>{title}</T>
      {sub && <T comp="p" style={{ color: C.mid, fontSize: 14, margin: "8px auto 0", maxWidth: 520 }}>{sub}</T>}
    </div>
  );
}
function Mini({ label, val, c }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ color: c, fontFamily: "var(--mono)", fontWeight: 800, fontSize: 17 }}>{val}</div>
      <div style={{ color: C.dim, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.5 }}><T>{label}</T></div>
    </div>
  );
}

/* About the creator — a brief profile + an invitation to bring Onset into a course or to
 * collaborate on research (AI for Psychology). Name & title stay verbatim (proper nouns); the
 * invitation copy is translatable. */
const PROFILE_URL = "https://www.polyu.edu.hk/apss/people/academic-staff/prof-hu-xiangen/";
const CONTACT_EMAIL = "xiangenhu@gmail.com";

function AboutModal({ onClose }) {
  return (
    <div style={ab.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={ab.modal} role="dialog" aria-modal="true" aria-label="About Onset">
        <button style={ab.close} onClick={onClose} aria-label={t("Close")}><X size={16} /></button>

        <div style={ab.head}>
          <img src={portrait} alt="Prof. Xiangen Hu" style={ab.photo} />
          <div>
            <div style={ab.name}>Prof. Xiangen Hu</div>
            <div style={ab.title}>DoERC &amp; Chair Professor of Learning Sciences and Technologies</div>
            <div style={ab.affil}>Department of Applied Social Sciences · The Hong Kong Polytechnic University</div>
          </div>
        </div>

        <T comp="p" style={ab.lead}>
          Onset is built to bring rigorous, hands-on cognitive psychology to the classroom and the lab —
          where students can feel a classic paradigm, then learn the science behind it.
        </T>

        <div style={ab.invites}>
          <div style={ab.invite}>
            <div style={{ ...ab.inviteIcon, background: C.indigo + "1f" }}><GraduationCap size={16} color={C.indigo} /></div>
            <div>
              <T comp="div" style={ab.inviteT}>Use it in your course</T>
              <T comp="div" style={ab.inviteB}>Want these paradigms tailored to your own cognitive psychology class? I'm glad to help adapt the studio, demos, and teaching material to your syllabus.</T>
            </div>
          </div>
          <div style={ab.invite}>
            <div style={{ ...ab.inviteIcon, background: C.green + "1f" }}><FlaskConical size={16} color={C.green} /></div>
            <div>
              <T comp="div" style={ab.inviteT}>Collaborate on research</T>
              <T comp="div" style={ab.inviteB}>Interested in working together — including AI for Psychology? Reach out to explore a project.</T>
            </div>
          </div>
        </div>

        <div style={ab.actions}>
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Onset — Experiment Studio")}`} style={ab.primary}>
            <Mail size={15} /> <T>Get in touch</T>
          </a>
          <a href={PROFILE_URL} target="_blank" rel="noreferrer" style={ab.ghost}>
            <ExternalLink size={14} /> <T>PolyU profile</T>
          </a>
        </div>
      </div>
    </div>
  );
}

const ab = {
  overlay: { position: "fixed", inset: 0, background: "rgba(6,9,13,0.86)", display: "grid", placeItems: "center", zIndex: 80, padding: 20, animation: "landingIn .25s ease" },
  modal: { position: "relative", width: "min(560px, 100%)", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16, padding: "26px 24px 22px", boxShadow: "0 28px 70px rgba(0,0,0,0.5)" },
  close: { position: "absolute", top: 12, insetInlineEnd: 12, display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: C.raised, color: C.mid, cursor: "pointer" },
  head: { display: "flex", gap: 16, alignItems: "center", marginBottom: 18 },
  photo: { width: 72, height: 92, borderRadius: 12, objectFit: "cover", objectPosition: "center top", border: `1px solid ${C.border}`, flexShrink: 0, background: C.raised },
  name: { color: C.hi, fontWeight: 800, fontSize: 20, letterSpacing: -0.3 },
  title: { color: C.amber, fontSize: 13, fontWeight: 600, marginTop: 3, lineHeight: 1.4 },
  affil: { color: C.mid, fontSize: 12.5, marginTop: 3, lineHeight: 1.4 },
  lead: { color: C.mid, fontSize: 14, lineHeight: 1.65, margin: "0 0 18px" },
  invites: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 },
  invite: { display: "flex", gap: 12, alignItems: "flex-start", padding: "13px 14px", borderRadius: 12, background: C.raised, border: `1px solid ${C.border}` },
  inviteIcon: { width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center", flexShrink: 0 },
  inviteT: { color: C.hi, fontWeight: 700, fontSize: 13.5, marginBottom: 3 },
  inviteB: { color: C.mid, fontSize: 12.5, lineHeight: 1.55 },
  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  primary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: "pointer", textDecoration: "none", fontFamily: "var(--ui)" },
  ghost: { display: "inline-flex", alignItems: "center", gap: 7, padding: "11px 16px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.hi, fontWeight: 600, fontSize: 13.5, cursor: "pointer", textDecoration: "none", fontFamily: "var(--ui)" },
};

const s = {
  page: { position: "absolute", inset: 0, overflowY: "auto", background: C.bg, color: C.hi, fontFamily: "var(--ui)", animation: "landingIn .45s ease" },
  nav: { position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", borderBottom: `1px solid ${C.border}`, background: "rgba(14,18,23,0.82)", backdropFilter: "blur(10px)" },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  logo: { width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, display: "grid", placeItems: "center", flexShrink: 0 },
  brandName: { fontWeight: 800, fontSize: 15.5, letterSpacing: -0.2 },
  navLinks: { display: "flex", alignItems: "center", gap: 22 },
  navLink: { color: C.mid, fontSize: 13.5, textDecoration: "none", fontWeight: 500 },
  signin: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 15px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.raised, color: C.hi, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--ui)" },

  hero: { display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 40, alignItems: "center", maxWidth: 1140, margin: "0 auto", padding: "64px 28px 56px" },
  heroL: { minWidth: 0 },
  chip: { display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: 999, border: `1px solid ${C.border}`, background: C.panel, color: C.mid, fontSize: 12, marginBottom: 20, fontFamily: "var(--mono)" },
  h1: { fontSize: 44, lineHeight: 1.1, fontWeight: 800, letterSpacing: -1, margin: "0 0 18px" },
  lead: { color: C.mid, fontSize: 16, lineHeight: 1.65, maxWidth: 520, margin: "0 0 28px" },
  heroCtas: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 24 },
  primary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 22px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--ui)" },
  secondary: { display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 20px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.hi, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", fontFamily: "var(--ui)" },
  trust: { display: "flex", gap: 18, flexWrap: "wrap" },
  trustItem: { display: "inline-flex", alignItems: "center", gap: 6, color: C.dim, fontSize: 12.5 },

  heroR: { minWidth: 0 },
  stageCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.45)" },
  stageBar: { display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: C.raised },
  stage: { background: "#0A0D11", height: 168, display: "grid", placeItems: "center" },
  timeline: { display: "flex", alignItems: "center", gap: 3, padding: "14px 14px 6px" },
  tl: { borderRadius: 8, border: "1px solid", padding: "7px 10px", display: "flex", flexDirection: "column", gap: 2 },
  metrics: { display: "flex", gap: 10, padding: "8px 16px 18px" },

  section: { maxWidth: 1080, margin: "0 auto", padding: "44px 28px" },
  demoLoading: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: C.dim, fontSize: 13, padding: "30px 0" },
  eyebrow: { color: C.amber, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4, fontFamily: "var(--mono)", marginBottom: 10 },
  h2: { fontSize: 30, fontWeight: 800, letterSpacing: -0.6, margin: 0 },

  fGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 },
  fCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 },
  fIcon: { width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", marginBottom: 14 },
  fTitle: { color: C.hi, fontWeight: 700, fontSize: 15.5, marginBottom: 7 },
  fBody: { color: C.mid, fontSize: 13.5, lineHeight: 1.6 },

  rGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 },
  rCard: { position: "relative", textAlign: "start", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, cursor: "pointer", fontFamily: "var(--ui)", color: C.hi, overflow: "hidden" },
  rDot: { width: 10, height: 10, borderRadius: 999, marginBottom: 14 },
  rName: { fontWeight: 800, fontSize: 16 },
  rDash: { color: C.dim, fontSize: 11.5, fontFamily: "var(--mono)", marginBottom: 12 },
  rBlurb: { color: C.mid, fontSize: 13, lineHeight: 1.55, marginBottom: 16 },
  rGo: { display: "inline-flex", alignItems: "center", gap: 5, color: C.hi, fontSize: 12.5, fontWeight: 600 },

  targets: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 22 },
  tCard: { textAlign: "center", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 14px" },
  honest: { display: "flex", gap: 12, alignItems: "flex-start", padding: "16px 18px", borderRadius: 12, background: "rgba(242,169,59,0.07)", border: "1px solid rgba(242,169,59,0.22)", color: C.mid, fontSize: 13.5, lineHeight: 1.6 },

  cta: { textAlign: "center", padding: "64px 28px", borderTop: `1px solid ${C.border}`, background: C.panel },
  ctaH: { fontSize: 28, fontWeight: 800, letterSpacing: -0.5, margin: "0 0 10px" },

  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "20px 28px", borderTop: `1px solid ${C.border}` },
  fStatus: { display: "flex", gap: 18, flexWrap: "wrap" },
  statItem: { display: "inline-flex", alignItems: "center", gap: 6, color: C.dim, fontSize: 11.5, fontFamily: "var(--mono)" },
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap');
:root{ --ui:'Inter',system-ui,-apple-system,'Segoe UI',sans-serif; --mono:'IBM Plex Mono',ui-monospace,'SF Mono',Menlo,monospace; }
*{ box-sizing:border-box; }
body{ margin:0; }
html{ scroll-behavior:smooth; }
@keyframes spin{ to{ transform:rotate(360deg); } }
.spin{ animation:spin .9s linear infinite; }
::-webkit-scrollbar{ width:10px; height:10px; }
::-webkit-scrollbar-thumb{ background:${C.border}; border-radius:6px; }
a:hover{ color:${C.hi}; }
.cta:hover{ filter:brightness(1.1); }
.ghost:hover{ border-color:${C.indigoDim}; background:${C.raised}; }
.card{ transition:transform .16s ease, border-color .16s ease, box-shadow .16s ease; }
.card:hover{ transform:translateY(-3px); border-color:${C.indigoDim}; box-shadow:0 12px 30px rgba(0,0,0,0.32); }
.roleCard{ transition:transform .16s ease, border-color .16s ease; }
.roleCard:hover{ transform:translateY(-3px); border-color:var(--accent); }
.roleCard:hover .rGo{ color:var(--accent); }
button:focus-visible, a:focus-visible{ outline:2px solid ${C.indigo}; outline-offset:2px; }
@keyframes floatK{ 0%,100%{ transform:translateY(0);} 50%{ transform:translateY(-8px);} }
.float{ animation:floatK 5s ease-in-out infinite; }
@keyframes popK{ 0%{ opacity:0; transform:scale(0.92);} 100%{ opacity:1; transform:scale(1);} }
.pop{ animation:popK .28s ease; }
@keyframes landingIn{ from{ opacity:0; } to{ opacity:1; } }
@media (prefers-reduced-motion: reduce){ .float,.pop{ animation:none; } html{ scroll-behavior:auto; } }
@media (max-width: 880px){
  .hero2col{ }
}
`;
